import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery, useApiPost, useApiPut, useApiDelete, useApiMutation, useApiBlobMutation } from '../../hooks/use-api';
import {
  PlatformTemplateListResponse,
  PlatformTemplateDetailResponse,
  CreatePlatformTemplateRequest,
  UpdatePlatformTemplateRequest,
  BlockOption,
  ExerciseOption,
  EquipmentOption
} from './types';

// Query Keys
export const platformTemplateKeys = {
  all: ['platform-templates'],
  lists: () => [...platformTemplateKeys.all, 'list'],
  list: (filters: Record<string, any>) => [...platformTemplateKeys.lists(), JSON.stringify(filters)],
  details: () => [...platformTemplateKeys.all, 'detail'],
  detail: (id: number) => [...platformTemplateKeys.details(), id.toString()],
  scopes: () => [...platformTemplateKeys.all, 'scopes'],
  structures: () => [...platformTemplateKeys.all, 'structures'],
};

// Hooks for fetching data
export function usePlatformTemplateList(
  params: {
    page?: number;
    pageSize?: number;
    search?: string;
    difficulty?: number;
    scope?: number;
    structure?: number;
    equipmentId?: number;
    includeInactive?: boolean;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  } = {}
) {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.difficulty) queryParams.append('difficulty', params.difficulty.toString());
  if (params.scope) queryParams.append('scope', params.scope.toString());
  if (params.structure) queryParams.append('structure', params.structure.toString());
  if (params.equipmentId) queryParams.append('equipmentId', params.equipmentId.toString());
  if (params.includeInactive) queryParams.append('includeInactive', params.includeInactive.toString());
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortDirection) queryParams.append('sortDirection', params.sortDirection);

  const endpoint = `/platform/templates${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  return useApiQuery<PlatformTemplateListResponse>(
    platformTemplateKeys.list(params),
    endpoint
  );
}

export function usePlatformTemplateDetail(id: number) {
  return useApiQuery<PlatformTemplateDetailResponse>(
    platformTemplateKeys.detail(id),
    `/platform/templates/${id}`,
    {
      enabled: !!id && id > 0,
    }
  );
}

export function usePlatformTemplateScopes() {
  return useApiQuery<Array<{ value: number; name: string }>>(
    platformTemplateKeys.scopes(),
    '/platform/templates/scopes'
  );
}

export function usePlatformTemplateStructures() {
  return useApiQuery<Array<{ value: number; name: string }>>(
    platformTemplateKeys.structures(),
    '/platform/templates/structures'
  );
}

// Block options for template creation (reusing platform blocks)
export function usePlatformBlockOptions() {
  return useApiQuery<{
    blocks: BlockOption[];
    pagination: { page: number; pageSize: number; totalCount: number; totalPages: number };
  }>(
    ['platform-blocks', 'options'],
    '/platform/blocks?pageSize=500&includeInactive=false'
  );
}

// Exercise options for template creation (reusing platform exercises)
export function usePlatformExerciseOptions() {
  return useApiQuery<{
    exercises: ExerciseOption[];
    pagination: { page: number; pageSize: number; totalCount: number; totalPages: number };
  }>(
    ['platform-exercises', 'options'],
    '/platform/exercises?pageSize=500&includeInactive=false'
  );
}

// Equipment options for dropdowns (reusing platform equipment)
export function usePlatformEquipmentOptions() {
  return useApiQuery<{
    equipment: EquipmentOption[];
    pagination: { page: number; pageSize: number; totalCount: number; totalPages: number };
  }>(
    ['platform-equipment', 'options'],
    '/platform/equipment?pageSize=100&includeInactive=false'
  );
}

// Hooks for mutations
export function useCreatePlatformTemplate() {
  const queryClient = useQueryClient();

  return useApiPost<{ id: number; message: string }, any, CreatePlatformTemplateRequest>(
    '/platform/templates',
    {
      onSuccess: () => {
        // Invalidate and refetch template list
        queryClient.invalidateQueries({ queryKey: platformTemplateKeys.lists() });
      },
    }
  );
}

export function useUpdatePlatformTemplate() {
  const queryClient = useQueryClient();

  return useApiPut<{ message: string }, any, UpdatePlatformTemplateRequest & { id: number }>(
    (variables) => `/platform/templates/${variables.id}`,
    {
      onSuccess: (data, variables) => {
        // Invalidate and refetch template list and specific item
        queryClient.invalidateQueries({ queryKey: platformTemplateKeys.lists() });
        queryClient.invalidateQueries({ queryKey: platformTemplateKeys.detail(variables.id) });
      },
    }
  );
}

export function useDeletePlatformTemplate() {
  const queryClient = useQueryClient();

  return useApiDelete<{ message: string }, any, { id: number }>(
    (variables) => `/platform/templates/${variables.id}`,
    {
      onSuccess: () => {
        // Invalidate and refetch template list
        queryClient.invalidateQueries({ queryKey: platformTemplateKeys.lists() });
      },
    }
  );
}

export function useReactivatePlatformTemplate() {
  const queryClient = useQueryClient();

  return useApiMutation<{ message: string }, any, { id: number }>(
    'POST',
    (variables) => `/platform/templates/${variables.id}/reactivate`,
    {
      onSuccess: (data, variables) => {
        // Invalidate and refetch template list and specific item
        queryClient.invalidateQueries({ queryKey: platformTemplateKeys.lists() });
        queryClient.invalidateQueries({ queryKey: platformTemplateKeys.detail(variables.id) });
      },
    }
  );
}

export function useReorderPlatformTemplates() {
  const queryClient = useQueryClient();

  return useApiMutation<{ message: string }, any, { templateIds: number[] }>(
    'POST',
    () => '/platform/templates/reorder',
    {
      onSuccess: () => {
        // Invalidate and refetch template list to reflect new order
        queryClient.invalidateQueries({ queryKey: platformTemplateKeys.lists() });
      },
    }
  );
}

// CSV Export/Import hooks
export function useExportPlatformTemplatesCsv() {
  return useApiBlobMutation<any, {
    includeInactive?: boolean;
    search?: string;
    difficulty?: number;
    scope?: number;
    structure?: number;
    primaryEquipmentId?: number;
  }>(
    'GET',
    (variables) => {
      const queryParams = new URLSearchParams();
      if (variables.includeInactive) queryParams.append('includeInactive', 'true');
      if (variables.search) queryParams.append('search', variables.search);
      if (variables.difficulty) queryParams.append('difficulty', variables.difficulty.toString());
      if (variables.scope) queryParams.append('scope', variables.scope.toString());
      if (variables.structure) queryParams.append('structure', variables.structure.toString());
      if (variables.primaryEquipmentId) queryParams.append('primaryEquipmentId', variables.primaryEquipmentId.toString());
      
      return `/platform/templates/export/csv${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    }
  );
}

export function useImportPlatformTemplatesCsv() {
  const queryClient = useQueryClient();

  return useApiMutation<
    { message: string; summary: { created: number; updated: number; skipped: number }; errors: string[] },
    any,
    { file: File }
  >(
    'POST',
    () => '/platform/templates/import/csv',
    {
      onSuccess: () => {
        // Invalidate and refetch template list after import
        queryClient.invalidateQueries({ queryKey: platformTemplateKeys.lists() });
      },
      requestType: 'multipart',
    }
  );
}